import React, { useRef, useState } from "react";
import {
  hslBackgroundForProfileId,
  normalizeProfileImageUrl,
  profileDisplayInitial,
  profileHasPhoto,
} from "../../utils/profileAvatarDisplay";
import { uploadProfileImage } from "../../utils/Network";
import ModalCropImage from "../dialogs/ModalCropImage";

/**
 * Participant profile photo: CMS-style letter avatar when no image, centered disk, camera upload + crop.
 * @param {'aside'|'linear'|'header'} variant — sidebar (120px), mobile strip (80px), or compact header chip.
 */
export default function UserProfileAvatarWidget({
  variant = "aside",
  profile,
  onProfileUpdated,
  showChangePhoto,
}) {
  const [localImage, setLocalImage] = useState(false);
  const [profileImageLoading, setProfileImageLoading] = useState(false);
  const [modalCropImageIsOpen, setModalCropImageIsOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(false);

  const imageUploadRef = useRef(null);

  if (!profile) {
    return null;
  }

  const canChangePhoto =
    showChangePhoto ?? profile.role !== "referee";

  const handleChanges = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    setModalCropImageIsOpen(true);
  };

  const onFileCropped = (file) => {
    setModalCropImageIsOpen(false);
    if (file) {
      setProfileImageLoading(true);
      uploadProfileImage(profile.id, file).then((data) => {
        if (data.image) {
          setLocalImage(false);
          onProfileUpdated(data);
        }
        setProfileImageLoading(false);
      });
    }
  };

  const diskClass =
    variant === "linear"
      ? "profile-image-linear-container bg-color-primary user-profile-avatar-widget__disk"
      : variant === "header"
      ? "profile-image-header-container bg-color-primary user-profile-avatar-widget__disk"
      : "profile-image-aside-container bg-color-primary user-profile-avatar-widget__disk";

  const showPhoto = !!(localImage || profileHasPhoto(profile));
  const initial = profileDisplayInitial(profile);
  const bgColor = hslBackgroundForProfileId(profile.id);
  const displayName =
    `${profile.first_name || ""} ${profile.last_name || ""}`.trim();

  return (
    <div
      className={`user-profile-avatar-widget user-profile-avatar-widget--${variant}`}
    >
      <div className={diskClass}>
        {showPhoto ? (
          <img
            alt={displayName || ""}
            className="user-profile-avatar-widget__img"
            src={
              localImage
                ? localImage
                : profile.image
                ? normalizeProfileImageUrl(profile.image)
                : undefined
            }
          />
        ) : (
          <div
            className="user-profile-avatar-widget__letter"
            style={{ backgroundColor: bgColor }}
            role="img"
            aria-label={displayName || initial}
          >
            {initial}
          </div>
        )}
        {canChangePhoto ? (
          <>
            <span
              className="profile-image-button profile-information-image-button"
              onClick={() =>
                imageUploadRef.current && imageUploadRef.current.click()
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  imageUploadRef.current && imageUploadRef.current.click();
                }
              }}
              role="button"
              tabIndex={0}
              title="Change photo"
            >
              <i className="fas fa-camera text-light" aria-hidden />
            </span>
            <input
              type="file"
              ref={imageUploadRef}
              name="profileimage"
              onChange={handleChanges}
              className="profile-image-input"
              accept="image/*"
            />
          </>
        ) : null}
        {profileImageLoading && (
          <div
            className="loading-overlay-showing border-radius-50 opacity-8"
            data-loading-overlay
            data-plugin-options="{'hideDelay': 500, 'effect': 'floatRings'}"
          >
            <div className="loading-overlay border-radius-50">
              <div className="bounce-loader">
                <div className="cssload-float-rings-loader">
                  <div className="cssload-float-rings-inner cssload-one" />
                  <div className="cssload-float-rings-inner cssload-two" />
                  <div className="cssload-float-rings-inner cssload-three" />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {canChangePhoto && selectedFile && (
        <ModalCropImage
          key={modalCropImageIsOpen ? "crop-open" : "crop-closed"}
          file={selectedFile}
          isOpen={modalCropImageIsOpen}
          setIsOpen={setModalCropImageIsOpen}
          onOutput={(image) => setLocalImage(image)}
          onSuccess={onFileCropped}
        />
      )}
    </div>
  );
}
